import type { CSSProperties } from "react";
import { memo } from "react";
import {
  ORGANIC_STEM_PATH,
  buildOrganicEyePath,
  resolveOrganicAvatar,
} from "./organic.js";

export interface ClusterMember {
  botId: string;
  color: string;
}

export interface OrganicClusterAvatarProps {
  members: readonly ClusterMember[];
  size?: number;
  className?: string;
  style?: CSSProperties;
}

const SLOTS = [
  { cx: 142, cy: 60, scale: 0.43 },
  { cx: 64, cy: 138, scale: 0.43 },
  { cx: 140, cy: 142, scale: 0.43 },
] as const;

/**
 * 2x2 multi-bot cluster view for groups.
 * Replicates OrganicGroupPresentation in iOS and renderClusterSvg in ui/bot-web.html.
 */
export const OrganicClusterAvatar = memo(function OrganicClusterAvatar({
  members,
  size = 48,
  className,
  style,
}: OrganicClusterAvatarProps) {
  const visible = members.slice(0, 3);

  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        userSelect: "none",
        ...style,
      }}
    >
      <svg
        viewBox="0 0 200 200"
        width={size}
        height={size}
        aria-hidden="true"
        style={{ overflow: "visible" }}
      >
        {visible.map((m, idx) => {
          const slot = SLOTS[idx] ?? SLOTS[0]!;
          const resolved = resolveOrganicAvatar(m.color, m.botId);
          const { shape, eyeColor, d } = resolved;
          const lx = 100 - shape.eyeGap;
          const rx = 100 + shape.eyeGap;

          return (
            <g
              key={m.botId || idx}
              transform={`translate(${slot.cx} ${slot.cy}) scale(${slot.scale}) rotate(${shape.tilt}) translate(-100 -100)`}
            >
              {/* White stroke outline so overlapping shapes are clearly separated */}
              <path
                d={d}
                fill="none"
                stroke="var(--canvas, #FFFFFF)"
                strokeWidth={22}
                strokeLinejoin="round"
              />
              {shape.hasStem ? (
                <path
                  d={ORGANIC_STEM_PATH}
                  transform="translate(100, 42.5)"
                  fill="#231F20"
                />
              ) : null}
              <path d={d} fill={resolved.color} />
              <g fill={eyeColor}>
                <path d={buildOrganicEyePath(lx, shape.eyeY, 0, 1.0, 1.25)} />
                <path d={buildOrganicEyePath(rx, shape.eyeY, 0, 1.0, 1.25)} />
              </g>
            </g>
          );
        })}
      </svg>
    </div>
  );
});
